import { resolveMediaUrl } from '@/config/env'
import type { MapLibreStyle } from '@/models/interfaces/map/FondLayerStyle'
import { resolveIconLoadUrl, stableMapIconImageId } from '@/services/map/mapIconCatalog'
import { apiClient } from '@/plugins/axios/api'
import type { Map as MapLibreMap } from 'maplibre-gl'

const ICON_URL_META = 'sigcom:mapIconContentUrl'

function getSymbolLayerIconUrl(layer: Record<string, unknown>): string | null {
  const meta = layer.metadata as Record<string, unknown> | undefined
  const url = meta?.[ICON_URL_META]
  return typeof url === 'string' && url.trim() !== '' ? url.trim() : null
}

/**
 * Resolves the actual binary image URL from an icon reference.
 *
 * - /media/... or blob:... → already a binary URL, return as-is.
 * - /api/media_objects/{id} → JSON metadata endpoint.
 *   Uses apiClient (axios) which injects the JWT Bearer token and has CORS configured.
 *   Extracts contentUrl from the response, returns resolveMediaUrl(contentUrl).
 *   The /media/... file is then loaded via the Vite proxy in DEV or directly in production.
 */
async function resolveIconBinaryUrl(absoluteUrl: string): Promise<string | null> {
  if (!absoluteUrl.includes('/api/media_objects/')) {
    return absoluteUrl
  }

  try {
    // Extract the relative API path so apiClient uses its configured baseURL.
    const apiPath = absoluteUrl.replace(/^https?:\/\/[^/]+/, '')
    const response = await apiClient.get<{ contentUrl?: string }>(apiPath)
    const contentUrl = (response.data.contentUrl ?? '').trim()
    if (!contentUrl) {
      console.warn('[map-icons] resolveIconBinaryUrl: empty contentUrl for', absoluteUrl)
      return null
    }
    return resolveMediaUrl(contentUrl)
  } catch (err) {
    console.warn('[map-icons] resolveIconBinaryUrl: failed for', absoluteUrl, err)
    return null
  }
}

/**
 * Loads raster images for symbol layers that reference uploaded media (metadata URL).
 * Must run after layers exist on the map. Idempotent per image id.
 *
 * When the stored URL is a /api/media_objects/{id} endpoint, the JSON metadata is
 * fetched first (public endpoint) to extract contentUrl, then the real image is loaded.
 */
export async function loadCustomIconImagesForMap(
  map: MapLibreMap,
  style: MapLibreStyle,
): Promise<void> {
  const layers = style.layers ?? []
  for (const layer of layers) {
    if (layer.type !== 'symbol') continue
    const raw = layer as unknown as Record<string, unknown>
    const urlRaw = getSymbolLayerIconUrl(raw)
    if (!urlRaw) continue
    const absoluteUrl = resolveMediaUrl(urlRaw)
    if (!absoluteUrl) continue
    const imageId = stableMapIconImageId(absoluteUrl)
    if (map.hasImage(imageId)) continue

    // Priority 1: session blob URL (upload within the same page-load — same-origin, no CORS).
    // Priority 2: dev Vite-proxy path (handled by resolveIconLoadUrl).
    const cachedUrl = resolveIconLoadUrl(absoluteUrl)
    const hasCachedUrl = cachedUrl !== absoluteUrl
    if (hasCachedUrl) {
      try {
        const loaded = await map.loadImage(cachedUrl)
        const data = loaded && typeof loaded === 'object' && 'data' in loaded ? loaded.data : loaded
        if (data && !map.hasImage(imageId)) {
          map.addImage(imageId, data as HTMLImageElement | ImageBitmap | ImageData)
        }
      } catch (error: unknown) {
        console.warn('[map-icons] loadImage (cached) failed:', absoluteUrl, error)
      }
      continue
    }

    // Priority 3: resolve the real binary URL — /api/media_objects/{id} URLs must be
    // resolved to their contentUrl first; /media/... URLs are used as-is.
    const binaryUrl = await resolveIconBinaryUrl(absoluteUrl)
    if (!binaryUrl) continue

    // In DEV, resolveIconLoadUrl rewrites the absolute /media/... URL to a relative
    // path so the Vite proxy forwards the request same-origin (avoids CORS).
    const loadUrl = resolveIconLoadUrl(binaryUrl)

    try {
      const loaded = await map.loadImage(loadUrl)
      const data = loaded && typeof loaded === 'object' && 'data' in loaded ? loaded.data : loaded
      if (data && !map.hasImage(imageId)) {
        map.addImage(imageId, data as HTMLImageElement | ImageBitmap | ImageData)
      }
    } catch (error: unknown) {
      console.warn('[map-icons] loadImage failed:', loadUrl, '(from', absoluteUrl + ')', error)
    }
  }
}

export { ICON_URL_META }
